import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import type { HandoffState, VerifyIdentificationResponse } from '@asko/shared';
import { ApiError, api } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { CameraCapture } from '../components/CameraCapture';
import { LangSwitch } from '../components/LangSwitch';

/**
 * Mijozning telefonida ochiladigan sahifa (QR orqali).
 *
 * Ataylab juda sodda: bir jumla ko'rsatma va kamera. Bu yerda mijoz
 * haqida hech qanday ma'lumot ko'rsatilmaydi — havola begonaga tushib
 * qolsa ham u hech narsa bilmaydi. Kirish ham talab qilinmaydi:
 * telefonda bizning hisobimiz yo'q, himoya havoladagi tokenda.
 */

/** Namuna rejimida sinash uchun tipik xatolik kodlari. */
const MOCK_FAILURES = [3, 21, 22, 2] as const;

export function MobileCapturePage() {
  const { token = '' } = useParams();
  const { t, apiMessage, myIdMessage } = useI18n();

  const [state, setState] = useState<HandoffState | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [failure, setFailure] = useState<VerifyIdentificationResponse | null>(null);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    api
      .get<HandoffState>(`/myid/handoff/${token}`)
      .then(setState)
      .catch((err: unknown) => {
        // «Muddati tugagan» deb faqat havolaning O'ZI yo'q bo'lganda
        // aytamiz. Boshqa xatoda (tarmoq, chegara) bu yolg'on bo'ladi:
        // mijoz sotuvchidan yangi QR so'raydi, eskisi esa butun edi.
        if (err instanceof ApiError && err.status === 404) {
          setState({ status: 'EXPIRED', mode: 'real', myidUrl: null });
          return;
        }
        setError(err instanceof ApiError ? err : null);
      });
  }, [token]);

  /**
   * MyID sahifasiga o'tish.
   *
   * Qaytish manzili bitta va u MyID'da ro'yxatdan o'tgan, shuning uchun
   * qaysi identifikatsiya ekanini o'zimiz eslab qolamiz: qaytgach
   * `MyIdCallbackPage` shu tokenni o'qiydi.
   */
  function goToMyId(url: string) {
    sessionStorage.setItem('myid:handoff', token);
    window.location.href = url;
  }

  async function handleCapture(photo: string, mockResultCode?: number) {
    setError(null);
    setFailure(null);
    setSubmitting(true);
    try {
      const result = await api.post<VerifyIdentificationResponse>(
        `/myid/handoff/${token}/photo`,
        { photo, ...(mockResultCode ? { mockResultCode } : {}) },
      );
      if (result.status === 'SUCCESS') setDone(true);
      else setFailure(result);
    } catch (err) {
      setError(err instanceof ApiError ? err : null);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mobile-shell">
      <header className="mobile-shell__head">
        <span className="app-header__brand">
          <img className="app-header__logo" src="/logo.png" alt="ASKO" />
        </span>
        <LangSwitch />
      </header>

      <main className="mobile-shell__body">
        {error && !state ? (
          <div className="card">
            <h1 className="section-title">{t('mobile.errorTitle')}</h1>
            <p className="section-hint">{apiMessage(error)}</p>
          </div>
        ) : done || state?.status === 'DONE' ? (
          <div className="card mobile-done">
            <div className="mobile-done__mark" aria-hidden="true">
              ✓
            </div>
            <h1 className="section-title">{t('mobile.doneTitle')}</h1>
            <p className="section-hint">{t('mobile.doneHint')}</p>
          </div>
        ) : state?.status === 'EXPIRED' ? (
          <div className="card">
            <h1 className="section-title">{t('mobile.expiredTitle')}</h1>
            <p className="section-hint">{t('mobile.expiredHint')}</p>
          </div>
        ) : (
          <div className="card">
            <h1 className="section-title">{t('mobile.title')}</h1>
            <p className="section-hint">{t('mobile.hint')}</p>

            {state?.myidUrl ? (
              // Real rejim: suratni MyID o'z sahifasida oladi
              <button
                className="btn btn--primary btn--wide"
                type="button"
                onClick={() => goToMyId(state.myidUrl!)}
              >
                {t('mobile.startMyId')}
              </button>
            ) : (
              state && (
                <CameraCapture
                  submitting={submitting}
                  onCapture={(photo) => handleCapture(photo)}
                  confirmLabel={t('mobile.send')}
                  extra={
                    // Namuna rejimida xatolik stsenariylarini shu yerda
                    // sinash mumkin. Haqiqiy rejimda rasm MyID
                    // sahifasida olinadi va bu yo'l ochilmaydi.
                    state.mode === 'mock'
                      ? (photo) => (
                          <div className="mock-actions">
                            <div className="mock-actions__fail">
                              <span className="mock-actions__label">{t('myid.mock.label')}</span>
                              {MOCK_FAILURES.map((code) => (
                                <button
                                  key={code}
                                  className="btn btn--small"
                                  type="button"
                                  onClick={() => handleCapture(photo, code)}
                                  disabled={submitting}
                                >
                                  {t(`myid.mock.${code}`)}
                                </button>
                              ))}
                            </div>
                          </div>
                        )
                      : undefined
                  }
                />
              )
            )}

            {failure && (
              <div className="alert alert--error" role="alert">
                {myIdMessage(failure.resultCode, failure.message)}
              </div>
            )}
            {error && (
              <div className="alert alert--error" role="alert">
                {apiMessage(error)}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
